import type React from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { BaseButton } from "shared/atoms/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { Tooltip } from "shared/molecules/Tooltip";

interface Props {
  text: string;
  visible: boolean;
  close: () => void;
  onConfirm: () => void;
  error?: string | null;
  setError: (value: React.SetStateAction<string | null>) => void;
  dataTestId: string;
}

export const ConfirmPopup: React.FC<Props> = ({ text, close, visible, onConfirm, error, setError, dataTestId }) => {
  const { t } = useTranslation();
  const { handleSubmit } = useForm();

  const onSubmit = async () => {
    try {
      onConfirm();
    } catch {
      setError(t("errors.generic"));
    }
  };

  const formComponent = () => {
    return (
      <FormContainer data-testid={`${dataTestId}_form`} onSubmit={handleSubmit(onSubmit)}>
        <TextContainer>
          {error ? <Notification variant="danger">{error}</Notification> : <Text>{text}</Text>}
        </TextContainer>
        <ButtonContainer>
          {error ? (
            <BaseButton text={t("buttons.ok")} onClick={close} />
          ) : (
            <>
              <BaseButton
                dataTestID={`${dataTestId}-close-button`}
                text={t("buttons.no")}
                onClick={close}
                variant="secondary"
                uppercase
              />
              <BaseButton dataTestID={`${dataTestId}-button`} text={t("buttons.yes")} type="submit" />
            </>
          )}
        </ButtonContainer>
      </FormContainer>
    );
  };

  return (
    <Tooltip requestClose={close} isOpen={visible} offset="top-right">
      {formComponent()}
    </Tooltip>
  );
};

const FormContainer = styled.form`
  display: flex;
  flex: 1;
  flex-direction: column;

  ${props => props.theme.belowBreakpoint} {
    flex-direction: row;
    height: 100%;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex: 0;
  min-width: 330px;
  ${props => props.theme.belowBreakpoint} {
    min-width: unset;
    flex: 1;
    align-items: center;
    padding: 0 ${props => props.theme.spacing.S_15};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 16px;
  margin: ${props => props.theme.spacing.S_10} 0;

  ${props => props.theme.belowBreakpoint} {
    margin: 0;
    justify-content: unset;
    box-sizing: border-box;
    height: 100%;
    flex-direction: column-reverse;
    border-radius: 0;
    border: 0;
    border-left: 1;
    border-style: solid;
    border-color: ${props => props.theme.colors.greys.silver};
  }
`;

const Text = styled.span`
  ${props => props.theme.font.info};
`;
